import { Injectable, inject } from '@angular/core';
import { parseApolloError } from '@app/common/utils';
import { Platform } from '@ionic/angular';
import { NavigationService } from '@services/navigation/navigation.service';
import { PushNotificationService } from '@services/notification/push-notification.service';
import {
    AuthResult,
    LoginGQL,
    LoginPatientInput,
    LogoutGQL,
    RefreshTokenFromBodyGQL,
    RefreshTokenFromCookieGQL,
    RegisterPatientGQL,
    RegisterPatientInput,
} from 'graphql/generated';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, Subscription, catchError, filter, iif, map, of, switchMap, tap, throwError, timer } from 'rxjs';
import { FrontendConfiguration } from 'src/configuration/config.patientportal';
import { PatientAuthData } from '../../models/patient/patient-auth-data';
import { PlatformService } from '../local/platform/platform.service';
import { SecureStorageService } from '../local/secure-storage/secure-storage.service';
import { localStorageDictionary } from './../../models/dictionaries/local-storage-keys-dictionary';
import { LocalStorageService } from './../local/local-storge/local-storge.service';

@Injectable({
    providedIn: 'root',
})
export class AuthStateService {
    private secureStorageService = inject(SecureStorageService);
    private platformService = inject(PlatformService);
    private platform = inject(Platform);
    private loginGQL = inject(LoginGQL);
    private refreshTokenFromBodyGQL = inject(RefreshTokenFromBodyGQL);
    private refreshTokenFromCookieGQL = inject(RefreshTokenFromCookieGQL);
    private registerGQL = inject(RegisterPatientGQL);
    private logoutGQL = inject(LogoutGQL);
    private localStorageService = inject(LocalStorageService);
    private pushNotifications = inject(PushNotificationService);
    private navService = inject(NavigationService);
    private accessTokenSubject = new BehaviorSubject<string>(this._getAuthTokenAndCheckExpirationTime());
    private hasValidRefreshTokenSubject = new BehaviorSubject<boolean | null>(null);
    private lastPauseTime: number | null = null;
    private silentRefreshTokenSubscription: Subscription | null = null;
    private frontendConfig = new FrontendConfiguration();
    constructor() {
        this.silentRefreshTokenSubscription = this.createSilentRefreshObservable().subscribe();

        this.platform.pause
            .pipe(
                // filter out situations when user minimises/resumes the app before logging in (on the login page)
                filter(() => this.isLogged() === true),
                tap(() => {
                    // clear subscription from silent token refresh. This must be done this way, and not for example with filter(()=>isPaused) on the silent refresh observable because
                    // when the app is resumed all paused actions invoke in unknown order and it behaves incorrectly
                    this.silentRefreshTokenSubscription?.unsubscribe();
                    this.silentRefreshTokenSubscription = null;
                    this.lastPauseTime = Date.now();
                })
            )
            .subscribe();

        this.platform.resume
            .pipe(
                // filter out situations when user minimises/resumes the app before logging in (on the login page)
                filter(() => this.isLogged() === true),
                switchMap(() => {
                    if (
                        this.lastPauseTime !== null &&
                        (Date.now() - this.lastPauseTime) / 1000 < this.frontendConfig.userIdleSeconds + this.frontendConfig.userTimeoutSeconds
                        // use the total user idle time as the max logged-in-when-paused time to simulate the idle system
                    ) {
                        // refresh token on app resume because we dont know how much time is left
                        // also subscribe to the silent refresh system
                        this.silentRefreshTokenSubscription?.unsubscribe();
                        return this.refreshToken().pipe(
                            tap(() => {
                                // subscribe after new token arrives, because it may be expired before, which will result in double refreshToken() call
                                this.silentRefreshTokenSubscription = this.createSilentRefreshObservable().subscribe();
                            })
                        );
                    } else {
                        // logout if max paused time exceeded
                        return this.logout();
                    }
                })
            )
            .subscribe();
    }
    private createSilentRefreshObservable() {
        return this.accessTokenSubject.pipe(
            switchMap((accessToken) => {
                if (!accessToken) {
                    return of(null); // on user logout accessToken is empty. Don't schedule a refresh then
                } else {
                    const tokenExpirationDate = this.getTokenExpirationDate(accessToken);
                    if (tokenExpirationDate.getTime() < Date.now()) return this.refreshToken(); //invalid token, unlikely to happen

                    const diff = tokenExpirationDate.getTime() - Date.now();
                    const expiration80Percent = Date.now() + 0.8 * diff;
                    const nextRefreshDate = new Date(expiration80Percent);

                    return timer(nextRefreshDate).pipe(
                        switchMap(() => {
                            console.debug('Silent Refresh');
                            return this.refreshToken();
                        })
                    );
                }
            })
        );
    }
    private getTokenExpirationDate(jwtToken: string): Date {
        const payload = jwtToken.split('.')[1];
        const decodedPayloadString = atob(payload);
        const decodedPayload = JSON.parse(decodedPayloadString);
        const expiration: number = decodedPayload['exp']; // seconds since 1970
        return new Date(expiration * 1000);
    }
    private refreshTokenFromSecureStorage(): Observable<AuthResult | null | undefined> {
        return this.secureStorageService.getItem('Refresh-Token').pipe(
            switchMap((refreshToken) => {
                if (!refreshToken) return of(null);
                else
                    return this.refreshTokenFromBodyGQL
                        .mutate({ variables: { input: { refreshToken: refreshToken! } } })
                        .pipe(map((result) => result.data?.refreshTokenFromBody.authResult));
            })
        );
    }
    private refreshTokenFromServerCookie(): Observable<AuthResult | null | undefined> {
        return this.refreshTokenFromCookieGQL.mutate().pipe(map((result) => result.data?.refreshTokenFromCookie.authResult));
    }
    public refreshToken() {
        return iif(() => this.platformService.isMobile(), this.refreshTokenFromSecureStorage(), this.refreshTokenFromServerCookie()).pipe(
            switchMap((authResult) => this.handleAuthResult(authResult)),
            catchError((err: unknown) => {
                console.log(err);
                const errors = parseApolloError(err);
                if (errors.length !== 0) {
                    const error = errors[0];
                    if (error.code === 'errorMessage.refreshToken.unauthorized') {
                        this.hasValidRefreshTokenSubject.next(false);
                    }
                }

                return throwError(() => err);
            })
        );
    }
    private handleAuthResult(authResult: AuthResult | null | undefined): Observable<AuthResult | null | undefined> {
        if (!authResult) return of(null);

        this.accessTokenSubject.next(authResult.jwtAccessToken);
        if (this.platformService.isWeb()) {
            authResult.jwtAccessToken
                ? this.localStorageService.set(localStorageDictionary.accessToken, authResult.jwtAccessToken)
                : this.localStorageService.remove(localStorageDictionary.accessToken);
        }

        if (this.platformService.isMobile() && !!authResult.refreshToken) {
            this.hasValidRefreshTokenSubject.next(true);
            return this.secureStorageService.setItem('Refresh-Token', authResult.refreshToken).pipe(map(() => authResult));
        } else return of(authResult);
    }
    public getHasValidRefreshToken() {
        return this.hasValidRefreshTokenSubject.asObservable();
    }
    public getAccessToken() {
        return this.accessTokenSubject.asObservable();
    }
    public getAccessTokenValue() {
        return this.accessTokenSubject.getValue();
    }
    public getIsLogged(): Observable<boolean> {
        return this.getAccessToken().pipe(map(Boolean));
    }
    public isLogged(): boolean {
        return !!this.accessTokenSubject.getValue();
    }
    public register(input: RegisterPatientInput) {
        return this.registerGQL.mutate({ variables: { input } }).pipe(switchMap((result) => this.handleAuthResult(result.data?.registerPatient.authResult)));
    }
    public login(input: LoginPatientInput) {
        return this.loginGQL.mutate({ variables: { input } }).pipe(switchMap((result) => this.handleAuthResult(result.data?.loginPatient.authResult)));
    }
    public logout() {
        const clearToken$ =
            this.platformService.isMobile() && !this.platformService.isWeb()
                ? this.pushNotifications.clearExistingFCMToken()
                : new Observable<void>((observer) => {
                      observer.next();
                      observer.complete();
                  });

        return clearToken$.pipe(
            switchMap(() => this._logout()),
            // catch errors from clearing Fcm token - will fail if the user's been logged out already by the idle service
            catchError((err) => this._logout())
        );
    }

    public notAuthorized() {
        return this._logout();
    }

    private _logout() {
        return this.logoutGQL.mutate().pipe(
            switchMap((result) => this.handleAuthResult({ jwtAccessToken: '', refreshToken: '' }).pipe(map(() => result))),
            catchError((err) => this.handleAuthResult({ jwtAccessToken: '', refreshToken: '' })),
            tap(() => {
                this.localStorageService.remove(localStorageDictionary.accessToken);
            }),
            switchMap((result) => {
                return this.navService.navigate(['/login']).then(() => result);
            })
        );
    }
    public getAuthData(): Observable<PatientAuthData | null> {
        return this.getAccessToken().pipe(
            map((accessToken: string) => {
                if (!accessToken) {
                    return null;
                }

                const payload = accessToken.split('.')[1];
                const decodedPayloadString = atob(payload);
                const decodedPayload = JSON.parse(decodedPayloadString);
                const patientAuthData: PatientAuthData = { email: decodedPayload['email'], id: decodedPayload['PatientId'] };
                return patientAuthData;
            })
        );
    }
    private _getAuthTokenAndCheckExpirationTime(): string {
        if (this.platformService.isWeb()) {
            const token = this.localStorageService.get(localStorageDictionary.accessToken);
            if (token !== null && token !== '') {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp && new Date(decodedToken.exp * 1000) > new Date()) {
                    return token ?? '';
                } else {
                    this.localStorageService.remove(localStorageDictionary.accessToken);
                }
            }
        }
        return '';
    }
}
